import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import Panel from '../../../Panels';
import Attribute from '../Attribute';
import {
  AbuseRegistryRecord,
  AbuseRegistrySearchObject,
} from '../../../../types/report';

interface AbuseRegistrySearchProps {
  search: AbuseRegistrySearchObject;
}

const toDisplayValue = (v: string | null | undefined): string | null =>
  v == null || v.toLowerCase() === 'null' ? null : v;

const groupByState = (
  records: AbuseRegistryRecord[],
): Record<string, AbuseRegistryRecord[]> =>
  records.reduce<Record<string, AbuseRegistryRecord[]>>((acc, record) => {
    const { registryState } = record;
    if (!acc[registryState]) acc[registryState] = [];
    acc[registryState].push(record);
    return acc;
  }, {});

const AbuseRegistrySearch = ({
  search,
}: AbuseRegistrySearchProps): React.ReactElement | null => {
  if (!search || search.status === 'canceled') return null;

  const displayStatus = search.status;
  const recordsByState = groupByState(search.records);
  const sortedStateEntries = Object.entries(recordsByState).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <Panel
      title={i18n.getStr('components.abuseRegistrySearch.title')}
      status={displayStatus}
    >
      {sortedStateEntries.map(([state, records], groupIndex) => (
        <div key={`state-group-${state}`}>
          <hr className={groupIndex > 0 ? 'mb-2 mt-3' : 'mt-0 mb-2'} />
          <strong className='d-block mb-2'>{state}</strong>
          <hr className='mt-0 mb-3' />
          {records.map((record, i) => (
            <div key={`record-${record.id || i + 1}`}>
              {i > 0 && <hr className='mb-2 mt-2' />}
              <Attribute
                attr={i18n.getStr(
                  'components.abuseRegistrySearch.subjectFullName',
                )}
                value={toDisplayValue(record.subjectFullName)}
              />
              <Attribute
                attr={i18n.getStr('components.abuseRegistrySearch.subjectDob')}
                value={toDisplayValue(record.subjectDob)}
              />
              {toDisplayValue(record.registryAuthority) && (
                <Attribute
                  attr={i18n.getStr(
                    'components.abuseRegistrySearch.registryAuthority',
                  )}
                  value={toDisplayValue(record.registryAuthority)}
                />
              )}
              {toDisplayValue(record.additionalNotes) && (
                <Attribute
                  attr={i18n.getStr(
                    'components.abuseRegistrySearch.additionalNotes',
                  )}
                  value={toDisplayValue(record.additionalNotes)}
                />
              )}
              {toDisplayValue(record.ingestionDate) && (
                <Attribute
                  attr={i18n.getStr(
                    'components.abuseRegistrySearch.ingestionDate',
                  )}
                  value={toDisplayValue(record.ingestionDate)}
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </Panel>
  );
};

AbuseRegistrySearch.propTypes = {
  search: PropTypes.object,
};

AbuseRegistrySearch.defaultProps = {
  search: null,
};

export default i18n.renderTranslation()(AbuseRegistrySearch);
